import { useMemo } from 'react';

import { ProductCustomizationModifiers } from 'features/menu/components/product-detail/product-customization/product-customization-modifiers';
import { CustomizationRenderItem } from 'features/menu/components/product-detail/product-customization/types';
import { getComboSlotItems } from 'features/menu/components/product-detail/product-customization/utils';
import { SelectorContainer } from 'features/menu/components/product-detail/styled';
import { useProductWizardContext } from 'features/menu/components/product-wizard';
import { DEFAULT_SLOT_KEY } from 'features/menu/components/product-wizard/types';
import { useProductWizardFlow } from 'features/menu/components/product-wizard/use-product-wizard-flow';
import { logButtonClick } from 'features/navigation/utils/analytics';
import { useStoreContext } from 'features/store';

import { ListCardGroup } from 'components/list-card-group';
import { useSelectedGridRow } from 'components/list-card-group/use-selected-grid-row';
import { ProductCard } from 'components/product-card';
import { useMenuCalories } from 'hooks/menu/use-menu-calories';
import { ClickEventComponentNames } from 'state/amplitude';
import { useMainMenuContext } from 'state/menu/main-menu';
import { ILocalizedMenuEntry } from 'state/unified-menu/types';
import { useUnifiedMenuContext } from 'state/unified-menu/unified-menu-context';
import { useMenuQueryOptions } from 'state/unified-menu/use-menu-query-options';
import { getEntryPrice, stringifySanityImage } from 'state/unified-menu/utils';

export const ProductCustomizationSides = () => {
  const { indexedMenu } = useUnifiedMenuContext();
  const { currentFlowStep } = useProductWizardFlow();
  const { slotsSelections, selectComboSlotItem } = useProductWizardContext();
  const menuQueryOptions = useMenuQueryOptions();
  const { formatCalories } = useMenuCalories();

  const { store } = useStoreContext();
  const { activeDayParts } = useMainMenuContext();
  const storeHasBurgersForBreakfast = Boolean(store.hasBurgersForBreakfast);

  const slotKey = currentFlowStep.slotKey!;
  const selectedSlot = slotsSelections?.[slotKey];

  const comboSides: ILocalizedMenuEntry[] = useMemo(
    () =>
      slotKey === DEFAULT_SLOT_KEY
        ? []
        : getComboSlotItems({
            menu: indexedMenu,
            slotKey,
            posServiceMode: menuQueryOptions.posServiceMode,
            activeDayParts,
            storeHasBurgersForBreakfast,
          }),
    [
      slotKey,
      indexedMenu,
      menuQueryOptions.posServiceMode,
      activeDayParts,
      storeHasBurgersForBreakfast,
    ]
  );

  const { selectedRow, handleItemPress } = useSelectedGridRow({
    items: comboSides,
    selectedItemId: selectedSlot?.itemId,
  });

  const selectedSidesCustomization = useMemo(() => {
    if (!selectedRow) {
      return null;
    }

    const selectedItem = comboSides.find(item => item.id === selectedRow.itemId);

    if (!selectedItem?.options?.entries?.length) {
      return null;
    }

    return <ProductCustomizationModifiers />;
  }, [selectedRow, comboSides]);

  return (
    <SelectorContainer>
      <ListCardGroup
        items={comboSides}
        selectedRow={selectedRow?.rowIndex}
        ItemCustomizationComponent={selectedSidesCustomization}
        renderItem={({ item, index, rowIndex }: CustomizationRenderItem) => (
          <ProductCard
            key={`side-${item.id}-${index}`}
            isSelected={item.id === selectedSlot?.itemId}
            productProps={{
              name: item.name?.locale,
              price: getEntryPrice(item, menuQueryOptions.posServiceMode),
              calories: formatCalories(item.nutrition?.calories?.def),
              image: stringifySanityImage(item.image),
            }}
            onProductPress={() => {
              logButtonClick({
                attributes: {
                  component: ClickEventComponentNames.PRODUCT_COMBO_SLOT_SELECTION_UPDATE,
                  name: item.name?.locale ?? '',
                  Id: item.id ?? '',
                },
              });
              selectComboSlotItem(slotKey, item.id);
              handleItemPress(item, rowIndex);
            }}
          />
        )}
      />
    </SelectorContainer>
  );
};
